import type { ResourceResponse, ThunkReturnType } from "types";
import { createAsyncThunk } from "infrastructure/store/lib";
import type { RootState, AsyncThunk } from "infrastructure/store";
import { ApiState } from "infrastructure/api";
import { ApiRouteTypes } from "consts/enpoints/api";
import { MenuItem } from "consts/waMessaging";
import { concat } from "util/support";
import { payloadCreator } from "util/api";
import type {
    WaMessagesResponse,
    WaMessagesSetupsResponse,
    WaMessageSetup,
    PhoneVerificationsResponse,
    FailedPhoneVerificationsResponse,
    WaTemplatesSetupsResponse,
    WaTemplateSetup
} from "./types";
import { sliceToken } from "./state";
import {
    getWaMessages,
    getWaMessagesSetups,
    createWaMessagesSetup,
    updateWaMessagesSetup,
    getPhoneVerifications,
    getFailedPhoneVerifications,
    getWaTemplatesSetups,
    createWaTemplatesSetup,
    updateWaTemplatesSetup
} from "./api";

export const getWaMessagesThunk: AsyncThunk<
    ThunkReturnType<WaMessagesResponse>,
    string | undefined,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.GetWaMessages]),
    payloadCreator(getWaMessages),
    {
        condition: (_, { getState }) => {
            const { waMessaging } = getState() as RootState;

            return ![ApiState.Pending].includes(waMessaging[MenuItem.WaMessages].tableLoadingState);
        }
    }
);

export const getWaMessagesSetupsThunk: AsyncThunk<
    ThunkReturnType<WaMessagesSetupsResponse>,
    string | undefined,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.GetWaMessagesSetups]),
    payloadCreator(getWaMessagesSetups),
    {
        condition: (_, { getState }) => {
            const { waMessaging } = getState() as RootState;

            return ![ApiState.Pending].includes(waMessaging[MenuItem.WaMessagesSetups].tableLoadingState);
        }
    }
);

export const createWaMessagesSetupThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<WaMessageSetup>>,
    Partial<WaMessageSetup>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PostWaMessagesSetups]),
    payloadCreator(createWaMessagesSetup),
    {
        condition: (_, { getState }) => {
            const { waMessaging } = getState() as RootState;

            return ![ApiState.Pending].includes(waMessaging[MenuItem.WaMessagesSetups].tableLoadingState);
        }
    }
);

export const updateWaMessagesSetupThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<WaMessageSetup>>,
    Partial<WaMessageSetup>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PutWaMessagesSetups]),
    payloadCreator(updateWaMessagesSetup),
    {
        condition: (_, { getState }) => {
            const { waMessaging } = getState() as RootState;

            return ![ApiState.Pending].includes(waMessaging[MenuItem.WaMessagesSetups].tableLoadingState);
        }
    }
);

export const getPhoneVerificationsThunk: AsyncThunk<
    ThunkReturnType<PhoneVerificationsResponse>,
    string | undefined,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.GetPhoneVerifications]),
    payloadCreator(getPhoneVerifications),
    {
        condition: (_, { getState }) => {
            const { waMessaging } = getState() as RootState;

            return ![ApiState.Pending].includes(waMessaging[MenuItem.PhoneVerifications].tableLoadingState);
        }
    }
);

export const getFailedPhoneVerificationsThunk: AsyncThunk<
    ThunkReturnType<FailedPhoneVerificationsResponse>,
    string | undefined,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.GetFailedPhoneVerifications]),
    payloadCreator(getFailedPhoneVerifications),
    {
        condition: (_, { getState }) => {
            const { waMessaging } = getState() as RootState;

            return ![ApiState.Pending].includes(waMessaging[MenuItem.FailedPhoneVerifications].tableLoadingState);
        }
    }
);

export const getWaTemplatesSetupsThunk: AsyncThunk<
    ThunkReturnType<WaTemplatesSetupsResponse>,
    string | undefined,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.GetWaTemplatesSetups]),
    payloadCreator(getWaTemplatesSetups),
    {
        condition: (_, { getState }) => {
            const { waMessaging } = getState() as RootState;

            return ![ApiState.Pending].includes(waMessaging[MenuItem.WaTemplatesSetups].tableLoadingState);
        }
    }
);

export const createWaTemplatesSetupThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<WaTemplateSetup>>,
    Partial<WaTemplateSetup>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PostWaTemplatesSetups]),
    payloadCreator(createWaTemplatesSetup),
    {
        condition: (_, { getState }) => {
            const { waMessaging } = getState() as RootState;

            return ![ApiState.Pending].includes(waMessaging[MenuItem.WaTemplatesSetups].tableLoadingState);
        }
    }
);

export const updateWaTemplatesSetupThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<WaTemplateSetup>>,
    Partial<WaTemplateSetup>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PutWaTemplatesSetups]),
    payloadCreator(updateWaTemplatesSetup),
    {
        condition: (_, { getState }) => {
            const { waMessaging } = getState() as RootState;

            return ![ApiState.Pending].includes(waMessaging[MenuItem.WaTemplatesSetups].tableLoadingState);
        }
    }
);
